import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { ToastContainer } from './components/ToastContainer';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { DashboardPage } from './pages/DashboardPage';
import { PlansPage } from './pages/PlansPage';
import { EmployeesPage } from './pages/EmployeesPage';
import { EmployeeFormPage } from './pages/EmployeeFormPage';
import { StoresPage } from './pages/StoresPage';
import { CustomersPage } from './pages/CustomersPage';
import RewardsPage from './pages/RewardsPage';
import { PointsPage } from './pages/PointsPage';
import { BillingPage } from './pages/BillingPage';
import CompanyRulesPage from './pages/CompanyRulesPage';
import { CustomerLoginPage } from './pages/CustomerLoginPage';
import { CustomerDashboardPage } from './pages/CustomerDashboardPage';
import { HomePage } from './pages/HomePage';
import { LandingPage } from './pages/LandingPage';
import { ContractPlanPage } from './pages/ContractPlanPage';
import { AdminDashboardPage } from './pages/AdminDashboardPage';
import { AdminPlansPage } from './pages/AdminPlansPage';
import { AdminCompaniesPage } from './pages/AdminCompaniesPage';
import { AdminFinancialPage } from './pages/AdminFinancialPage';
import { AdminSettingsPage } from './pages/AdminSettingsPage';
import { AdminPaymentSettingsPage } from './pages/AdminPaymentSettingsPage';
import { AdminSEOPage } from './pages/AdminSEOPage';
import { AdminCrmPage } from './pages/AdminCrmPage';
import { CrmPage } from './pages/CrmPage';
import { AdminBlogPage } from './pages/AdminBlogPage';
import { AdminContactPage } from './pages/AdminContactPage';
import { AdminProfilePage } from './pages/AdminProfilePage';
import { AdminSiteContentPage } from './pages/AdminSiteContentPage';
import { AdminLoyaltyPage } from './pages/AdminLoyaltyPage';
import { SubscriptionManagementPage } from './pages/SubscriptionManagementPage';
import { InvoicesPage } from './pages/InvoicesPage';
import { CardsManagementPage } from './pages/CardsManagementPage';
import { ProfilePage } from './pages/ProfilePage';
import { AdvancedReportsPage } from './pages/AdvancedReportsPage';
import { LoyaltyTiersPage } from './pages/LoyaltyTiersPage';
import { LoyaltyInsightsPage } from './pages/LoyaltyInsightsPage';
import { IntegrationsPage } from './pages/IntegrationsPage';
import { PaymentNotifications } from './components/PaymentNotifications';
import { CustomerAuthProvider } from './contexts/CustomerAuthContext';
import { AdminAuthProvider } from './contexts/AdminAuthContext';
import { CustomerProtectedRoute } from './components/CustomerProtectedRoute';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AdminProtectedRoute } from './components/AdminProtectedRoute';
import { GlobalStyles } from './styles/GlobalStyles';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { PaymentCallbackPage } from './pages/PaymentCallbackPage';
import { UnsubscribePage } from './pages/UnsubscribePage';
import { TermsPage } from './pages/TermsPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { ContactPage } from './pages/ContactPage';
import { BlogPage } from './pages/BlogPage';
import { BlogPostPage } from './pages/BlogPostPage';
import { api } from './services/api';
import { applySeoConfig } from './utils/seoConfig';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  useEffect(() => {
    const loadSeoConfig = async () => {
      try {
        const response = await api.get('/seo-config');
        if (response.data?.success && response.data?.data) {
          applySeoConfig(response.data.data);
        }
      } catch (error) {
        console.error('Erro ao carregar configurações de SEO:', error);
      }
    };

    loadSeoConfig();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <AuthProvider>
          <AdminAuthProvider>
            <CustomerAuthProvider>
            <Router>
              <GlobalStyles />
              <ToastContainer />
              <PaymentNotifications />
              <Routes>
              {/* Landing page comercial */}
              <Route path="/" element={<LandingPage />} />

              {/* Seleção de módulos */}
              <Route path="/app" element={<HomePage />} />
              
              {/* Rotas públicas */}
              <Route path="/app/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/plans" element={<PlansPage />} />
              <Route path="/contract/:planName" element={<ContractPlanPage />} />
              <Route path="/unsubscribe" element={<UnsubscribePage />} />
              <Route path="/termos-de-uso" element={<TermsPage />} />
              <Route path="/politica-de-privacidade" element={<PrivacyPage />} />
              <Route path="/contato" element={<ContactPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />

              {/* Rotas de recuperação de senha */}
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/reset-password/employee" element={<ResetPasswordPage />} />
              <Route path="/reset-password/admin" element={<ResetPasswordPage />} />

              {/* Rotas de callback de pagamento */}
              <Route path="/payment/success" element={<PaymentCallbackPage />} />
              <Route path="/payment/pending" element={<PaymentCallbackPage />} />
              <Route path="/payment/failure" element={<PaymentCallbackPage />} />

              {/* Rotas do cliente */}
              <Route path="/customer/login" element={<CustomerLoginPage />} />
              <Route path="/customer/dashboard" element={
                <CustomerProtectedRoute>
                  <CustomerDashboardPage />
                </CustomerProtectedRoute>
              } />
              
              {/* Rotas protegidas */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            } />
            
            <Route path="/employees" element={
              <ProtectedRoute>
                <EmployeesPage />
              </ProtectedRoute>
            } />
            
            <Route path="/employees/new" element={
              <ProtectedRoute>
                <EmployeeFormPage />
              </ProtectedRoute>
            } />
            
            <Route path="/employees/:id/edit" element={
              <ProtectedRoute>
                <EmployeeFormPage />
              </ProtectedRoute>
            } />
            
            <Route path="/stores" element={
              <ProtectedRoute>
                <StoresPage />
              </ProtectedRoute>
            } />
            
            <Route path="/customers" element={
              <ProtectedRoute>
                <CustomersPage />
              </ProtectedRoute>
            } />
            
            <Route path="/rewards" element={
              <ProtectedRoute>
                <RewardsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/points" element={
              <ProtectedRoute>
                <PointsPage />
              </ProtectedRoute>
            } />

            <Route path="/billing" element={
              <ProtectedRoute>
                <BillingPage />
              </ProtectedRoute>
            } />

            <Route path="/crm" element={
              <ProtectedRoute>
                <CrmPage />
              </ProtectedRoute>
            } />

            <Route path="/reports" element={
              <ProtectedRoute>
                <AdvancedReportsPage />
              </ProtectedRoute>
            } />

            <Route path="/billing/subscription" element={
              <ProtectedRoute>
                <SubscriptionManagementPage />
              </ProtectedRoute>
            } />

            <Route path="/billing/invoices/:subscriptionId" element={
              <ProtectedRoute>
                <InvoicesPage />
              </ProtectedRoute>
            } />

            <Route path="/billing/cards" element={
              <ProtectedRoute>
                <CardsManagementPage />
              </ProtectedRoute>
            } />

            <Route path="/profile" element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            } />

            <Route path="/company-rules" element={
              <ProtectedRoute>
                <CompanyRulesPage />
              </ProtectedRoute>
            } />

            <Route path="/loyalty/tiers" element={
              <ProtectedRoute>
                <LoyaltyTiersPage />
              </ProtectedRoute>
            } />

            <Route path="/loyalty/insights" element={
              <ProtectedRoute>
                <LoyaltyInsightsPage />
              </ProtectedRoute>
            } />

            <Route path="/integrations" element={
              <ProtectedRoute>
                <IntegrationsPage />
              </ProtectedRoute>
            } />

            {/* Rotas protegidas de administrador */}
            <Route path="/admin/dashboard" element={
              <AdminProtectedRoute>
                <AdminDashboardPage />
              </AdminProtectedRoute>
            } />

            <Route path="/admin/plans" element={
              <AdminProtectedRoute>
                <AdminPlansPage />
              </AdminProtectedRoute>
            } />

            <Route path="/admin/companies" element={
              <AdminProtectedRoute>
                <AdminCompaniesPage />
              </AdminProtectedRoute>
            } />

            <Route path="/admin/financial" element={
              <AdminProtectedRoute>
                <AdminFinancialPage />
              </AdminProtectedRoute>
            } />

            <Route path="/admin/settings" element={
              <AdminProtectedRoute>
                <AdminSettingsPage />
              </AdminProtectedRoute>
            } />

            <Route path="/admin/site-content" element={
              <AdminProtectedRoute>
                <AdminSiteContentPage />
              </AdminProtectedRoute>
            } />

            <Route path="/admin/profile" element={
              <AdminProtectedRoute>
                <AdminProfilePage />
              </AdminProtectedRoute>
            } />

            <Route path="/admin/payment-settings" element={
              <AdminProtectedRoute>
                <AdminPaymentSettingsPage />
              </AdminProtectedRoute>
            } />

              <Route path="/admin/seo" element={
                <AdminProtectedRoute>
                  <AdminSEOPage />
                </AdminProtectedRoute>
              } />
              <Route path="/admin/crm" element={
                <AdminProtectedRoute>
                  <AdminCrmPage />
                </AdminProtectedRoute>
              } />
              <Route path="/admin/loyalty" element={
                <AdminProtectedRoute>
                  <AdminLoyaltyPage />
                </AdminProtectedRoute>
              } />
              <Route path="/admin/blog" element={
                <AdminProtectedRoute>
                  <AdminBlogPage />
                </AdminProtectedRoute>
              } />
              <Route path="/admin/contato" element={
                <AdminProtectedRoute>
                  <AdminContactPage />
                </AdminProtectedRoute>
              } />

            {/* Redirecionamento padrão */}
            
            {/* Página não encontrada */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
            </Router>
            </CustomerAuthProvider>
          </AdminAuthProvider>
        </AuthProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}

export default App;
